import axios from 'axios';

// Environment-based configuration
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


// Generic paginated fetcher for list endpoints
export const fetchAllPaginated = async (endpoint) => {
  let url = `${baseUrl}${endpoint}`;
  let all = [];
  try {
    while (url) {
      const { data } = await api.get(url);
      all = all.concat(data.results);
      url = data.next;
    }
    return all;
  } catch (error) {
    throw error;
  }
};

// Single page fetcher for paginated endpoints (for pagination UI)
export const fetchPaginatedPage = async (endpoint, page, pageSize = 10) => {
  const url = `${baseUrl}${endpoint}?page=${page}&page_size=${pageSize}`;
  const { data } = await api.get(url);
  return data;
};

// Products
export const fetchAllProducts = () => fetchAllPaginated('/api/products/');
export const fetchProductsPage = (page, pageSize) => fetchPaginatedPage('/api/products/', page, pageSize);
export const createProduct = (product) => api.post(`${baseUrl}/api/products/`, product);

// Product Variants
export const fetchAllProductVariants = () => fetchAllPaginated('/api/product-variants/');
export const fetchProductVariantsPage = (page, pageSize) => fetchPaginatedPage('/api/product-variants/', page, pageSize);
export const createProductVariant = (variant) => api.post(`${baseUrl}/api/product-variants/`, variant);
export const restockProductVariant = (restock) => api.post(`${baseUrl}/api/restocks/`, restock);
export const searchProductVariants = (searchTerm) => api.get(`${baseUrl}/api/product-variants/?search=${encodeURIComponent(searchTerm)}`);

// Sales
export const fetchAllSales = () => fetchAllPaginated('/api/sales/');
export const fetchSalesPage = (page, pageSize) => fetchPaginatedPage('/api/sales/', page, pageSize);
export const paySaleDue = (saleId, payment) => api.post(`${baseUrl}/api/sales-payment/${saleId}/`, payment);
export const returnSaleItem = (saleId, data) => api.post(`${baseUrl}/api/sales-return/${saleId}/`, data);
export const refundSale = (saleId) => api.post(`${baseUrl}/api/refund/${saleId}/`);
export const fetchSales = (page = 1) => api.get(`${baseUrl}/api/sales/?page=${page}`);
export const createSale = (saleData) => api.post(`${baseUrl}/api/sales/`, saleData);
export const fetchSaleDetails = (saleId) => api.get(`${baseUrl}/api/sales/${saleId}/`).then(res => res.data);

// Customers
export const fetchAllCustomers = () => fetchAllPaginated('/api/customers/');
export const fetchCustomerById = (id) => api.get(`${baseUrl}/api/customers/${id}/`).then(res => res.data);
export const createCustomer = (customer) => api.post(`${baseUrl}/api/customers/`, customer);
export const searchCustomers = (searchTerm) => api.get(`${baseUrl}/api/customers/?search=${encodeURIComponent(searchTerm)}`);

// Transactions
export const fetchAllTransactions = () => fetchAllPaginated('/api/transactions/');
export const fetchTransactionsPage = (page, pageSize) => fetchPaginatedPage('/api/transactions/', page, pageSize);

// Expenses
export const fetchAllExpenses = () => fetchAllPaginated('/api/expenses/');
export const fetchExpensesPage = (page, pageSize) => fetchPaginatedPage('/api/expenses/', page, pageSize);
export const createExpense = (expense) => api.post(`${baseUrl}/api/expenses/`, expense);

// Expense Categories
export const createExpenseCategory = (category) => api.post(`${baseUrl}/api/expense-categories/`, category);

// Profits
export const fetchSalesProfitPage = (page, pageSize) => fetchPaginatedPage('/api/sales-profit/', page, pageSize);
export const fetchProfitByDatePage = (page, pageSize) => fetchPaginatedPage('/api/sales-profit-by-date/', page, pageSize);
export const fetchAllExpenseCategories = () => fetchAllPaginated('/api/expense-categories/');
export const fetchAllProfits = () => fetchAllPaginated('/api/sales-profit/');
export const fetchAllProfitByDate = () => fetchAllPaginated('/api/sales-profit-by-date/');

// Auth
export const login = async (username, password) => {
  // Returns the response from the login endpoint
  return api.post(`${baseUrl}/api/token/`, { username, password });
};

// Daily Analysis
export const fetchDailyAnalysis = (startDate, endDate, page = 1, pageSize = 31) => {
  let url = `${baseUrl}/api/daily-analysis/?page=${page}&page_size=${pageSize}`;
  if (startDate) url += `&start_date=${startDate}`;
  if (endDate) url += `&end_date=${endDate}`;
  return api.get(url);
};

// Sales Profit data
export const fetchSalesProfit = (startDate, endDate) => {
  let url = `${baseUrl}/api/sales-profit/`;
  if (startDate || endDate) {
    url += '?';
    if (startDate) url += `start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
  }
  return api.get(url);
};

// Sales Profit by Date data
export const fetchSalesProfitByDate = (startDate, endDate) => {
  let url = `${baseUrl}/api/sales-profit-by-date/`;
  if (startDate || endDate) {
    url += '?';
    if (startDate) url += `start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
  }
  return api.get(url);
};

// Example usage: pass accountId in headers or as a param if needed
// axios.get(`${baseUrl}/api/endpoint/`, { headers: { 'X-Account-ID': accountId } })

export default api;
